import { type CSSProperties } from "react";
import {
  Check,
  GripVertical,
  Lock,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { DetailField, DetailFieldsSection } from "@/api/types";

type DetailSection = DetailFieldsSection;
type DetailSectionField = DetailField;

interface FieldVisibilityCustomizerProps {
  section: DetailSection;
  /** Effective ordering for this section. The popover renders rows
   *  in this order; the parent card iterates the same list and
   *  filters by `isVisible`, so the two stay in lockstep. */
  orderedFields: DetailSectionField[];
  visibleSet: Set<string>;
  onToggle: (key: string) => void;
  onReorder: (activeKey: string, overKey: string) => void;
  onReset: () => void;
}

/**
 * Per-card "customise fields" trigger + popover. Each row carries
 * a drag handle, a checkbox and the field label; locked rows are
 * draggable (operators may want to position scoring fields
 * wherever) but their checkbox is disabled. Toggle and reorder
 * actions are instantaneous — the parent hook persists to
 * localStorage and the card re-renders with the new layout on the
 * next paint.
 */
export function FieldVisibilityCustomizer({
  section,
  orderedFields,
  visibleSet,
  onToggle,
  onReorder,
  onReset,
}: FieldVisibilityCustomizerProps) {
  // Pointer + keyboard sensors mirror the rest of the app's dnd
  // surfaces (PipelineSection, TaskKanbanBoard) — keyboard support
  // matters for accessibility and the small distance threshold
  // keeps a click on the handle from registering as a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Customise ${section.label} fields`}
          className="rounded-md p-1 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 focus-visible:opacity-100 group-hover/card:opacity-100 data-[state=open]:opacity-100"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between border-b border-slate-900/[0.06] px-4 py-2.5">
          <h3 className="text-sm font-semibold text-slate-800">
            {section.label}
          </h3>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 transition-colors hover:text-indigo-600"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedFields.map((f) => f.key)}
              strategy={verticalListSortingStrategy}
            >
              {orderedFields.map((field) => (
                <SortableFieldRow
                  key={field.key}
                  field={field}
                  checked={
                    !!field.locked || visibleSet.has(field.key)
                  }
                  onToggle={onToggle}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface SortableFieldRowProps {
  field: DetailSectionField;
  checked: boolean;
  onToggle: (key: string) => void;
}

function SortableFieldRow({
  field,
  checked,
  onToggle,
}: SortableFieldRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.key });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // While dragging, lift the row visually so it sits on top of
    // its neighbours during the gap-shift animation.
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
  };

  const handle = (
    <button
      type="button"
      aria-label={`Reorder ${field.label}`}
      className="cursor-grab touch-none p-0.5 text-slate-300 hover:text-slate-500 active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  );

  if (field.locked) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              ref={setNodeRef}
              style={style}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-500"
            >
              {handle}
              <span
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 bg-slate-100 text-slate-400"
                aria-label="Always visible"
              >
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <span className="flex-1 truncate">{field.label}</span>
              <Lock className="h-3 w-3 shrink-0 text-slate-400" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-xs">
            Used for scoring
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
    >
      {handle}
      <button
        type="button"
        onClick={() => onToggle(field.key)}
        className="flex flex-1 items-center gap-2 text-left"
      >
        <span
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
            checked
              ? "border-indigo-500 bg-indigo-500 text-white"
              : "border-slate-300 bg-white",
          )}
        >
          {checked && <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
        <span className="truncate">{field.label}</span>
      </button>
    </div>
  );
}
